import AppLayout from "../layout/AppLayout";
import Pagination from "rc-pagination";
import { Spinner } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useAlert } from "react-alert";
import "rc-pagination/assets/index.css";
import regenerateToken from "../regenerateToken";
import axios from "axios";
import Loader from "../loader/loader";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";
import { FaSave, FaSearch, FaChevronLeft, FaChevronRight, FaFileExport, FaFileImport, FaDownload, FaPlus, FaCheck, FaTimes, FaTrashAlt } from "react-icons/fa";
import './profileMapping.css';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

const existingProfiles = [
  "Liver Profile",
  "Allergy Panel",
  "Thyroid Profile",
  "Anemia Studies",
  "Kidney Profile",
  "Electrolyte Profile",
  "Diabetes Monitoring",
  "Urinalysis",
  "Blood Group",
  "Blood Clotting",
  "Vitamin Profile",
  "Arthritis Screening",
  "Blood Counts",
  "Lipid Profile",
  "Prothrombin Time",
  "Hormones",
  "Semen Culture and Sensitivity",
  "Cancer Profile",
  "Viral Infections",
  "Infectious Diseases",
  "Hepatitis",
  "STD Profile",
  "Bacterial Infections",
  "Stool Analysis",
  "Semen Analysis",
  "Immunity",
  "Cardiac Profile",
  "Autoimmune Disorders",
  "Tumour Marker Test",
  "Pancreas",
  "Hemoglobin Electrophoresis",
  "Inflammation",
  "Toxic elements",
  "BMI & BP",
  "COVID",
  "Blood Disorder",
  "Bone Health",
  "Heart Assure",
  "Hypertension",
  "Malaria Profile",
  "Mineral Profile",
  "Neurological Disorders",
  "Pregnancy",
  "Fertility Profile",
  "Cockroach, German",
  "Cat",
  "Dog",
  "Horse",
  "Camel",
  "Penicillium Notatum",
  "Caldosporium Herbarium",
  "Aspergillus Fumigatus",
  "Candida Albicans",
  "Alternaria Alternata",
  "Protein Profile",
  "Radiology",
  "Arterial Blood Gas Analysis",
  "Hair Loss",
  "Peripheral Blood Film",
  "Dengue Profile",
  "Peripheral Blood Smear",
  "COVID ANTIBODY",
  "Male Cancer Profile",
];


const ProfileMapping = () => {
  const countPerPage = 10;
  const alert = useAlert();

  const [currentPage, setCurrentPage] = useState(1);
  const [parameterList, setParameterList] = useState({});
  const [paginatedData, setPaginatedData] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [exportLoader, setExportLoader] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [search, setSearch] = useState("");

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newParameterName, setNewParameterName] = useState("");
  const [newProfile, setNewProfile] = useState("");

  const getExistingProfileMapping = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "GET",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile",
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const existingMappingConfig = {
      headers: {
        authorization: idToken,
      },
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile",
      method: "GET",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const existingProfileMappingResponse = await axios(existingMappingConfig);
    return existingProfileMappingResponse;
  };

  useEffect(() => {
    (async function () {
      setPageLoading(true);
      try {
        const response = await getExistingProfileMapping();
        const statusCode = response.status;

        if (statusCode === 401) {
          await regenerateToken();
          const retryResponse = await getExistingProfileMapping();
          if (retryResponse?.data?.isSuccess) {
            const mapping = retryResponse.data?.data?.profileMapping || {};
            const keys = Object.keys(mapping);
            setParameterList(mapping);
            setPaginatedData(keys.slice(0, countPerPage));
            setFilteredTests(keys);
          } else {
            setHasError(true);
          }
        } else if (response?.data?.isSuccess) {
          const mapping = response?.data?.data?.profileMapping || {};
          const keys = Object.keys(mapping);
          setParameterList(mapping);
          setPaginatedData(keys.slice(0, countPerPage));
          setFilteredTests(keys);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Failed to load profile mapping:", error);
        setHasError(true);
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  const updatingPages = (p) => {
    const finalCount = p * countPerPage;
    const initialCount = finalCount - countPerPage;
    setPaginatedData(filteredTests.slice(initialCount, finalCount));
    setCurrentPage(p);
  };

  const filteringTests = (text) => {
    setSearch(text);
    const tests = Object.keys(parameterList).filter((each) =>
      each.toLowerCase().includes(text.toLowerCase())
    );

    setFilteredTests(tests);
    setPaginatedData(tests.slice(0, countPerPage));
    setCurrentPage(1); // Reset to first page on search
  };

  const handlingUpdate = (parameter, profile) => {
    const profileMappingObject = { ...parameterList }; // Create a copy to be safe
    profileMappingObject[parameter] = profile;
    setParameterList(profileMappingObject);
  };

  const handleConfirmation = async () => {
    setUpdateLoader(true);
    try {
      let response = await apiCallForUpdatingProfileMapping();
      if (response.status === 401) {
        await regenerateToken();
        response = await apiCallForUpdatingProfileMapping();
      }
      if (response.data?.isSuccess) {
        alert.success("Profile mapping saved successfully!");
        window.location.reload();
      } else {
        alert.error("Failed to save profile mapping.");
      }
    } catch (e) {
      console.error(e);
      alert.error("Network Error: Failed to save mapping. Please check your connection.");
    } finally {
      setUpdateLoader(false);
    }
  };

  const apiCallForUpdatingProfileMapping = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "POST",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile",
        data: parameterList,
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const config = {
      headers: {
        authorization: idToken,
      },
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile",
      method: "POST",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
      data: JSON.stringify(parameterList),
    };
    const response = await axios(config);
    return response;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonArray = XLSX.utils.sheet_to_json(worksheet);

        const importedMapping = jsonArray.reduce((acc, row) => {
          const testName = row["Test Name"];
          const profileName = row["Profile Name"];
          if (testName && profileName && String(testName).trim() && String(profileName).trim()) {
            acc[String(testName).trim()] = String(profileName).trim();
          }
          return acc;
        }, {});

        setParameterList((prev) => ({ ...prev, ...importedMapping }));
        alert.success("Profile mapping imported. Review and save changes.");
      } catch (error) {
        console.error("Error processing Excel file:", error);
        alert.error("Failed to read the Excel file. Please ensure it has columns 'Test Name' and 'Profile Name'.");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const handleDownloadSample = () => {
    const keys = Object.keys(parameterList);
    const sampleKeys = keys.slice(0, 5);
    const dataForExcel = sampleKeys.map((key) => ({
      "Test Name": key,
      "Profile Name": parameterList[key] || "",
    }));
    if (dataForExcel.length === 0) {
      dataForExcel.push({ "Test Name": "Example Test 1", "Profile Name": "Lipid Profile" });
      dataForExcel.push({ "Test Name": "Example Test 2", "Profile Name": "Liver Profile" });
    }
    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProfileMapping");
    XLSX.writeFile(wb, "ProfileMapping_Sample.xlsx");
  };

  // Fetch Parameter Mapping data
  const handleAddNewParameter = () => {
    const trimmedName = newParameterName.trim();
    const trimmedProfile = newProfile.trim();

    if (!trimmedName) {
      alert.error("Please enter a parameter name.");
      return;
    }

    if (!trimmedProfile) {
      alert.error("Please enter or select a profile for the new parameter.");
      return;
    }

    if (parameterList[trimmedName] !== undefined) {
      alert.error("This parameter already exists in the mapping.");
      return;
    }

    const updatedList = { ...parameterList, [trimmedName]: trimmedProfile };
    setParameterList(updatedList);

    // Refresh the filtering list so the new parameter appears
    const keys = Object.keys(updatedList);
    setFilteredTests(keys);
    setSearch("");
    setPaginatedData(keys.slice(0, countPerPage));
    setCurrentPage(1);

    setIsAddingNew(false);
    setNewParameterName("");
    setNewProfile("");

    alert.success("Parameter added locally. Please Save Changes to apply it to the database.");
  };

  const handleCancelNewParameter = () => {
    setIsAddingNew(false);
    setNewParameterName("");
    setNewProfile("");
  };

  // Handler to delete a parameter's profile mapping
  const handleDeleteProfileMapping = (test) => {
    const updatedList = { ...parameterList };
    delete updatedList[test];
    setParameterList(updatedList);

    // Refresh filtered list and pagination
    const keys = Object.keys(updatedList).filter((each) =>
      each.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredTests(keys);
    const finalCount = currentPage * countPerPage;
    const initialCount = finalCount - countPerPage;
    setPaginatedData(keys.slice(initialCount, finalCount));

    // If current page is now empty, go back one page
    if (keys.slice(initialCount, finalCount).length === 0 && currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      const newFinal = newPage * countPerPage;
      const newInitial = newFinal - countPerPage;
      setPaginatedData(keys.slice(newInitial, newFinal));
    }
  };

  const getParameterMappingData = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "GET",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter",
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const config = {
      headers: {
        authorization: idToken,
      },
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter",
      method: "GET",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const response = await axios(config);
    return response;
  };

  // Invert parameter mapping from { ID: Name } to { Name: [IDs] }
  const invertParameterMapping = (idMapping) => {
    const inverted = {};
    Object.keys(idMapping).forEach((id) => {
      const parameterName = idMapping[id];
      if (inverted[parameterName]) {
        inverted[parameterName].push(id);
      } else {
        inverted[parameterName] = [id];
      }
    });
    return inverted;
  };

  // Process data for CSV export
  const processDataForCSV = (profileMapping, parameterMapping, includeUnmapped) => {
    const invertedParamMapping = invertParameterMapping(parameterMapping);
    const csvData = [];

    // Get all unique parameter names from both mappings
    const allParameters = new Set([
      ...Object.keys(profileMapping),
      ...Object.keys(invertedParamMapping),
    ]);

    allParameters.forEach((parameterName) => {
      // Get profile name and normalize it
      let profileName = profileMapping[parameterName];

      // Treat null, undefined, empty string, or "Select Profile" as unmapped
      if (!profileName || profileName === "Select Profile" || profileName.trim() === "") {
        profileName = "(Not Assigned)";
      }

      const parameterIDs = invertedParamMapping[parameterName] || [];

      // If excluding unmapped, skip parameters without profile assignment
      // Strict check for boolean false
      if (includeUnmapped === false && profileName === "(Not Assigned)") {
        return;
      }

      csvData.push({
        profileName,
        parameterName,
        parameterIDs: parameterIDs.join(", "),
      });
    });

    // Sort by profile name, then by parameter name
    csvData.sort((a, b) => {
      if (a.profileName === b.profileName) {
        return a.parameterName.localeCompare(b.parameterName);
      }
      // Put "(Not Assigned)" at the end
      if (a.profileName === "(Not Assigned)") return 1;
      if (b.profileName === "(Not Assigned)") return -1;
      return a.profileName.localeCompare(b.profileName);
    });

    return csvData;
  };

  // Escape CSV field (handle commas, quotes, newlines)
  const escapeCSVField = (field) => {
    if (field == null) return "";
    const stringField = String(field);
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringField.includes(",") || stringField.includes('"') || stringField.includes("\n")) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  // Generate CSV string
  const generateCSV = (data) => {
    const headers = ["Profile Name", "Parameter Name", "Parameter Mapped IDs"];
    const csvRows = [headers.map(escapeCSVField).join(",")];

    data.forEach((row) => {
      const csvRow = [
        escapeCSVField(row.profileName),
        escapeCSVField(row.parameterName),
        escapeCSVField(row.parameterIDs),
      ].join(",");
      csvRows.push(csvRow);
    });

    // Add UTF-8 BOM for Excel compatibility
    return "\uFEFF" + csvRows.join("\n");
  };

  // Trigger CSV download
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Main export handler - directly exports excluding unmapped
  const handleExportCSV = async () => {
    // Always exclude unmapped parameters as per requirement
    const includeUnmapped = false;

    setExportLoader(true);

    try {
      // Fetch both APIs in parallel
      const [profileResponse, parameterResponse] = await Promise.all([
        getExistingProfileMapping(),
        getParameterMappingData(),
      ]);

      // Handle token expiration - check if either call needs refresh
      const needsTokenRefresh = profileResponse.status === 401 || parameterResponse.status === 401;

      let profileData;
      let parameterData;

      if (needsTokenRefresh) {
        // Regenerate token once
        await regenerateToken();

        // Re-fetch both mappings with fresh token
        const [retryProfileResponse, retryParameterResponse] = await Promise.all([
          getExistingProfileMapping(),
          getParameterMappingData(),
        ]);

        // Validate profile retry response
        if (!retryProfileResponse?.data?.isSuccess) {
          throw new Error('Failed to fetch profile mapping after token refresh');
        }
        profileData = retryProfileResponse.data?.data?.profileMapping;
        if (!profileData || Object.keys(profileData).length === 0) {
          throw new Error('Profile mapping data is empty after token refresh');
        }

        // Validate parameter retry response
        if (!retryParameterResponse?.data?.isSuccess) {
          throw new Error('Failed to fetch parameter mapping after token refresh');
        }
        parameterData = retryParameterResponse.data?.data?.idMapping;
        if (!parameterData || Object.keys(parameterData).length === 0) {
          throw new Error('Parameter mapping data is empty after token refresh');
        }
      } else {
        // No token refresh needed - validate original responses
        if (!profileResponse?.data?.isSuccess) {
          throw new Error('Failed to fetch profile mapping');
        }
        profileData = profileResponse.data?.data?.profileMapping || {};

        if (!parameterResponse?.data?.isSuccess) {
          throw new Error('Failed to fetch parameter mapping');
        }
        parameterData = parameterResponse.data?.data?.idMapping || {};
      }

      // Process data
      const csvData = processDataForCSV(profileData, parameterData, includeUnmapped);

      if (csvData.length === 0) {
        alert.show("No data available to export.", { type: "warning" });
        setExportLoader(false);
        return;
      }

      // Generate CSV
      const csvContent = generateCSV(csvData);

      // Create filename with current date
      const today = new Date().toISOString().split("T")[0];
      const filename = `Profile_Mapping_Export_${today}.csv`;

      // Download
      downloadCSV(csvContent, filename);

      // Show success message
      alert.success(
        <span style={{ textTransform: "none" }}>
          CSV exported successfully! ({csvData.length} rows)
        </span>
      );
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert.error("Failed to export CSV. Please try again.");
    } finally {
      setExportLoader(false);
    }
  };

  return (
    <AppLayout>
      {hasError ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
          <div className="text-center">
            <h3 className="mb-3 text-secondary">Unable to Load Data</h3>
            <p className="text-muted mb-4">There was an issue fetching the profile mapping configuration.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      ) : pageLoading ? (
        <Loader />
      ) : (
        <div className="prm-container">
          {/* Header */}
          <div className="prm-header">
            <div className="prm-title">
              <h2>Profile Mapping</h2>
              <p>Map your internal master tests to specific display profiles.</p>
            </div>
            <div className="prm-actions">
              <button
                onClick={handleDownloadSample}
                className="prm-btn-outline"
                type="button"
              >
                <FaDownload /> Download Sample
              </button>
              <label htmlFor="prm-file-upload" className="prm-btn-outline" style={{ marginBottom: 0, cursor: "pointer" }}>
                <FaFileImport /> Import Excel
              </label>
              <input
                id="prm-file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
              <button
                onClick={handleExportCSV}
                className="prm-btn-outline"
                disabled={exportLoader}
              >
                {exportLoader ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><FaFileExport /> Export CSV</>
                )}
              </button>

              <button
                onClick={() => handleConfirmation()}
                className="prm-btn-primary"
                disabled={updateLoader}
              >
                {updateLoader ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><FaSave /> Save Changes</>
                )}
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="prm-controls">
            <div className="prm-search-wrapper">
              <FaSearch className="prm-search-icon" />
              <input
                className="prm-search-input"
                placeholder="Search for tests..."
                value={search}
                onChange={(event) => filteringTests(event.target.value)}
              />
            </div>
            <div className="prm-stats">
              Total Mapped: {Object.values(parameterList).filter((v) => v && String(v).trim() !== "").length} / {Object.keys(parameterList).length}
            </div>
          </div>

          {/* List Header */}
          <div className="prm-list-header">
            <div>Master Test Name</div>
            <div>Assigned Profile</div>
            <div className="text-center">Action</div>
          </div>

          <div className="prm-list-container">
            {isAddingNew && (
              <div className="prm-row" style={{ backgroundColor: "#f8fafc", borderLeft: "4px solid var(--primary-color, #4f46e5)" }}>
                <div className="prm-input-wrapper">
                  <input
                    type="text"
                    className="prm-input"
                    placeholder="Enter new master test name"
                    value={newParameterName}
                    onChange={(e) => setNewParameterName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="prm-select-wrapper" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <input
                    list="new-profile-options"
                    className="prm-input"
                    placeholder="Select or type a profile name..."
                    value={newProfile}
                    onChange={(e) => setNewProfile(e.target.value)}
                  />
                  <datalist id="new-profile-options">
                    {existingProfiles.map((eachProfile) => (
                      <option key={`new-${eachProfile}`} value={eachProfile} />
                    ))}
                  </datalist>
                  <button
                    className="prm-action-btn prm-btn-confirm"
                    onClick={handleAddNewParameter}
                    title="Confirm"
                  >
                    <FaCheck />
                  </button>
                  <button
                    className="prm-action-btn prm-btn-cancel"
                    onClick={handleCancelNewParameter}
                    title="Cancel"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            )}
            {paginatedData.length > 0 ? (
              paginatedData.map((test, index) => (
                <div key={test} className="prm-row">
                  <div className="prm-test-name">
                    <label htmlFor={`profile-dropdown-${index}`} className="visually-hidden">
                      Select profile for {test}
                    </label>
                    {test}
                  </div>
                  <div className="prm-select-wrapper">
                    <input
                      id={`profile-dropdown-${index}`}
                      list={`profile-list-${index}`}
                      className="prm-input prm-profile-compact"
                      placeholder="Select or type profile..."
                      value={parameterList[test] || ""}
                      onChange={(event) => {
                        handlingUpdate(test, event.target.value);
                      }}
                      aria-label={`Profile for ${test}`}
                    />
                    <datalist id={`profile-list-${index}`}>
                      {existingProfiles.map((eachProfile) => (
                        <option key={`${eachProfile}`} value={eachProfile} />
                      ))}
                    </datalist>
                  </div>
                  <div className="prm-action-column text-center">
                    <button
                      className="prm-action-btn prm-btn-delete"
                      title="Remove this mapping"
                      onClick={() => handleDeleteProfileMapping(test)}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-5 text-muted">No tests found matching your search.</div>
            )}
          </div>

          {/* Pagination */}
          <Pagination
            onChange={(p) => updatingPages(p)}
            pageSize={countPerPage}
            total={filteredTests.length}
            current={currentPage}
            showTitle={false}
            itemRender={(current, type, element) => {
              if (type === 'prev') {
                return (
                  <span className="prm-pagination-icon">
                    <FaChevronLeft />
                  </span>
                );
              }
              if (type === 'next') {
                return (
                  <span className="prm-pagination-icon">
                    <FaChevronRight />
                  </span>
                );
              }
              return element;
            }}
          />
        </div>
      )}
    </AppLayout>
  );
};

export default ProfileMapping;
